import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Box,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider
} from '@mui/material';
import {
  Visibility,
  Print,
  CheckCircle,
  Pending,
  Cancel,
  Assessment
} from '@mui/icons-material';
import { COLORS } from '../theme/colors';
import { useAuth } from '../contexts/AuthContext';
import { getUserQuotes, getQuoteById } from '../services/quoteService';

const MyQuotes = () => {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quoteDetailDialog, setQuoteDetailDialog] = useState(false);

  useEffect(() => {
    if (user) {
      fetchQuotes();
    }
  }, [user]);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const userQuotes = await getUserQuotes();
      setQuotes(userQuotes);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      setError('Failed to load your quotes');
    } finally {
      setLoading(false);
    }
  };

  const viewQuoteDetails = async (quoteId) => {
    try {
      const quote = await getQuoteById(quoteId);
      setSelectedQuote(quote);
      setQuoteDetailDialog(true);
    } catch (error) {
      console.error('Error fetching quote details:', error);
      setError('Failed to load quote details');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'in-review':
        return 'info';
      case 'quoted':
        return 'primary';
      case 'accepted':
        return 'success';
      case 'rejected':
        return 'error';
      case 'completed':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Pending />;
      case 'in-review':
        return <Assessment />;
      case 'quoted':
        return <Print />;
      case 'accepted':
      case 'completed':
        return <CheckCircle />;
      case 'rejected':
        return <Cancel />;
      default:
        return <Pending />;
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      let date;
      
      // Handle Firestore Timestamp object
      if (timestamp && typeof timestamp === 'object' && timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      }
      // Handle Firestore Timestamp with _seconds/_nanoseconds (serialized format)
      else if (timestamp && typeof timestamp === 'object' && timestamp._seconds) {
        date = new Date(timestamp._seconds * 1000);
      }
      // Handle Firestore Timestamp with seconds/nanoseconds
      else if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      }
      // Handle timestamp as string or number
      else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
        date = new Date(timestamp);
      }
      // Handle already parsed Date object
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      else {
        date = new Date(timestamp);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Error parsing date';
    }
  };

  const handlePlaceOrder = async (quote) => {
    try {
      // Create an order item for the 3D printing service
      const orderItem = {
        id: quote.id,
        name: `3D Printing Service - ${quote.fileName}`,
        price: quote.finalQuote,
        quantity: 1,
        type: '3d-printing',
        customization: {
          fileName: quote.fileName,
          material: quote.printSettings?.material || 'PLA',
          layerHeight: quote.printSettings?.layerHeight || '0.2mm',
          infill: quote.printSettings?.infill || '20%',
          supports: quote.printSettings?.supports ? 'Yes' : 'No',
          modelUrl: quote.modelUrl,
          quoteId: quote.id
        }
      };

      // Store the order item in session storage for the checkout process
      sessionStorage.setItem('checkout-items', JSON.stringify([orderItem]));
      sessionStorage.setItem('checkout-source', '3d-printing-quote');
      sessionStorage.setItem('quote-id', quote.id);

      // Navigate to checkout
      window.location.href = '/checkout';
    } catch (error) {
      console.error('Error placing order:', error);
      alert('Failed to place order. Please try again.');
    }
  };

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">
          Please log in to view your quote requests.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={700} mb={4} color={COLORS.text.primary}>
        My Quote Requests
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : quotes.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" mb={2}>
            No quote requests yet
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Upload your first STL file to get a quote for 3D printing
          </Typography>
          <Button
            variant="contained"
            href="/services/3d-printing"
            sx={{ backgroundColor: COLORS.primary.main }}
          >
            Get Your First Quote
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {quotes.map((quote) => (
            <Grid item xs={12} md={6} lg={4} key={quote.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" fontWeight={600} noWrap>
                      {quote.fileName || 'Unknown File'}
                    </Typography>
                    <Chip
                      icon={getStatusIcon(quote.status)}
                      label={quote.status.charAt(0).toUpperCase() + quote.status.slice(1)}
                      color={getStatusColor(quote.status)}
                      size="small"
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Material: {quote.printSettings?.material || 'N/A'}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Estimated: ₹{quote.quote?.total?.toFixed(2) || 'N/A'}
                  </Typography>

                  {quote.finalQuote && (
                    <Typography variant="body2" color="primary" fontWeight={600} mb={1}>
                      Final Quote: ₹{quote.finalQuote.toFixed(2)}
                    </Typography>
                  )}

                  <Typography variant="caption" color="text.secondary">
                    Submitted: {formatDate(quote.createdAt)}
                  </Typography>

                  <Box mt={2}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => viewQuoteDetails(quote.id)}
                      fullWidth
                    >
                      View Details
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Quote Detail Dialog */}
      <Dialog
        open={quoteDetailDialog}
        onClose={() => setQuoteDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedQuote && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Quote Details</Typography>
                <Chip
                  icon={getStatusIcon(selectedQuote.status)}
                  label={selectedQuote.status.charAt(0).toUpperCase() + selectedQuote.status.slice(1)}
                  color={getStatusColor(selectedQuote.status)}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                {/* File Information */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    File Information
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>File Name:</strong> {selectedQuote.fileName}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Dimensions:</strong> {selectedQuote.modelAnalysis?.boundingBox ? 
                      `${selectedQuote.modelAnalysis.boundingBox.x.toFixed(1)} × ${selectedQuote.modelAnalysis.boundingBox.y.toFixed(1)} × ${selectedQuote.modelAnalysis.boundingBox.z.toFixed(1)} mm` : 'N/A'}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Volume:</strong> {selectedQuote.modelAnalysis?.volume?.toFixed(2) || 'N/A'} cm³
                  </Typography>
                </Grid>

                {/* Print Settings */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    Print Settings
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Material:</strong> {selectedQuote.printSettings?.material}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Layer Height:</strong> {selectedQuote.printSettings?.layerHeight}mm
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Infill:</strong> {selectedQuote.printSettings?.infill}%
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Supports:</strong> {selectedQuote.printSettings?.supports ? 'Yes' : 'No'}
                  </Typography>
                </Grid>

                {/* Cost Breakdown */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={600} mb={2}>
                    Cost Breakdown
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Item</strong></TableCell>
                          <TableCell align="right"><strong>Amount</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Material Cost</TableCell>
                          <TableCell align="right">₹{selectedQuote.quote?.materialCost?.toFixed(2) || 'N/A'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Labor & Machine Time</TableCell>
                          <TableCell align="right">₹{selectedQuote.quote?.laborCost?.toFixed(2) || 'N/A'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Subtotal</TableCell>
                          <TableCell align="right">₹{selectedQuote.quote?.subtotal?.toFixed(2) || 'N/A'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Tax (18% GST)</TableCell>
                          <TableCell align="right">₹{selectedQuote.quote?.taxes?.toFixed(2) || 'N/A'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Total</strong></TableCell>
                          <TableCell align="right"><strong>₹{selectedQuote.quote?.total?.toFixed(2) || 'N/A'}</strong></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Final Quote */}
                {selectedQuote.finalQuote && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      <Typography variant="subtitle2" fontWeight={600}>
                        Final Quote: ₹{selectedQuote.finalQuote.toFixed(2)}
                      </Typography>
                    </Alert>
                    {selectedQuote.status === 'quoted' && (
                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          onClick={() => handlePlaceOrder(selectedQuote)}
                          sx={{ mr: 2 }}
                        >
                          Place Order
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          Click to proceed with payment and place your 3D printing order
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                )}

                {/* Order Information */}
                {selectedQuote.orderId && selectedQuote.status === 'accepted' && (
                  <Grid item xs={12}>
                    <Alert severity="success">
                      <Typography variant="subtitle2" fontWeight={600}>
                        Order Placed Successfully!
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Order ID: <strong>{selectedQuote.orderId}</strong>
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mt: 1 }}
                        component="a"
                        href={`/orders/${selectedQuote.orderId}`}
                      >
                        View Order Details
                      </Button>
                    </Alert>
                  </Grid>
                )}

                {/* Notes */}
                {(selectedQuote.customerNotes || selectedQuote.adminNotes) && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    {selectedQuote.customerNotes && (
                      <>
                        <Typography variant="subtitle2" fontWeight={600} mb={1}>
                          Your Notes:
                        </Typography>
                        <Typography variant="body2" mb={2}>
                          {selectedQuote.customerNotes}
                        </Typography>
                      </>
                    )}
                    {selectedQuote.adminNotes && (
                      <>
                        <Typography variant="subtitle2" fontWeight={600} mb={1}>
                          Admin Notes:
                        </Typography>
                        <Typography variant="body2">
                          {selectedQuote.adminNotes}
                        </Typography>
                      </>
                    )}
                  </Grid>
                )}

                {/* Timestamps */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Submitted: {formatDate(selectedQuote.createdAt)}
                  </Typography>
                  {selectedQuote.updatedAt && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Last Updated: {formatDate(selectedQuote.updatedAt)}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setQuoteDetailDialog(false)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default MyQuotes;
